import { ref, onMounted, computed } from 'vue';
import type { Shipment } from '@/domains/shipment/types/Shipment';
import {
  updateShipment,
  getAllShipmentsFromCache,
  getShipmentsFromCacheByRouteId,
  upsertShipments,
  observeAllShipments,
  type ShipmentUpdate,
} from '@/domains/shipment/repositories/shipmentRepository';
import { useConstants } from '@/domains/constants';
import { useApiQuery } from '@/composables/useApi';
import { shipmentsEndpoints } from '@/services/api/apiConfig';
import type { ApiResponse } from '@/types/api';
import { useAutoUnsubscribe } from '@/composables/useAutoUnsubscribe';
import { useDateFormat } from '@/composables/useDateFormat';
import { useAuthStore } from '@/domains/auth/stores/useAuthStore';
import { getDeviceLocation, type DeviceLocation } from '@/utils/getDeviceLocation';
import { getAddressFromCacheById } from '@/domains/address/repositories/addressRepository';

const shipments = ref<Shipment[]>([]);
const error = ref<Error | null>(null);

export const useShipments = () => {
  // Dependencies
  const { trackSubscription } = useAutoUnsubscribe();
  const { actions: constantsActions } = useConstants();
  const { actions: dateActions } = useDateFormat();
  const authStore = useAuthStore();

  // computed statuses and constants
  const loadedStatus = computed(() => constantsActions.getShipmentStatusByCode('L'));
  const transLoadedStatus = computed(() => constantsActions.getShipmentStatusByCode('TL'));
  const deliveredStatus = computed(() => constantsActions.getShipmentStatusByCode('D'));
  const notOnTrailerStatus = computed(() => constantsActions.getShipmentStatusByCode('NOT'));
  const hplsInputType = computed(() => constantsActions.getInputTypeByCode('HPLS'));

  // Loaded or Trans-loaded: still on the truck and eligible for delivery scan.
  const pendingDeliveryStatusIds = computed(() => {
    const ids = new Set<number>();
    if (loadedStatus.value?.id != null) ids.add(loadedStatus.value.id);
    if (transLoadedStatus.value?.id != null) ids.add(transLoadedStatus.value.id);
    return ids;
  });

  const isPendingDeliveryStatus = (statusCodeId: number | null | undefined): boolean => {
    if (statusCodeId == null) return false;
    return pendingDeliveryStatusIds.value.has(statusCodeId);
  };

  const shipmentsQuery = useApiQuery<ApiResponse<Shipment[]>, Error>({
    endpoint: shipmentsEndpoints.LIST(),
    enabled: false, // Only fetch when explicitly called via refetch
    queryKey: ['shipments'],
  });

  const fetchShipmentsFromApi = async (): Promise<Shipment[]> => {
    const result = await shipmentsQuery.refetch();
    const response = result.data;
    if (!response?.data) {
      throw new Error('No shipment data available in API response');
    }
    return response.data;
  };

  // === SELECTORS ===

  // Route-based
  const getShipmentsByRouteId = (routeId: number): Shipment[] => {
    return (shipments.value ?? []).filter((s) => s.routeId === routeId);
  };

  const normalizeScanToken = (raw: string): string => raw.trim();

  /** Resolve by tracking number first, then by container/asset code (case-insensitive). */
  const getShipmentByRouteIdAndScanCode = (routeId: number, rawScan: string): Shipment | null => {
    const normalizedCode = normalizeScanToken(rawScan);
    if (!normalizedCode) return null;
    const routeShipments = getShipmentsByRouteId(routeId);

    const matchByTracking = routeShipments.find(
      (s) => normalizeScanToken(s.trackingNumber) === normalizedCode
    );
    if (matchByTracking) return matchByTracking;

    const normalizedCodeLower = normalizedCode.toLowerCase();
    const assetMatches = routeShipments.filter(
      (s) =>
        Boolean(s.assetCode) &&
        normalizeScanToken(s.assetCode ?? '').toLowerCase() === normalizedCodeLower
    );
    if (assetMatches.length === 0) return null;

    const roots = assetMatches.filter((s) => s.parentShipmentId == null);
    if (roots.length === 0) return null;

    // Multiple roots can share the same asset on a route (bad/legacy data). Pick the lowest Id so
    // container scans resolve deterministically; reduce uses an explicit initial value for TS/lint.
    const first = roots[0]!;
    return roots.slice(1).reduce<Shipment>((a, b) => (a.id < b.id ? a : b), first);
  };

  const getShipmentById = (shipmentId: number): Shipment | null => {
    return shipments.value.find((s) => s.id === shipmentId) ?? null;
  };

  // Route + destination
  const getShipmentsByRouteAndDestinationId = (addressId: number, routeId?: number): Shipment[] => {
    const source = routeId ? getShipmentsByRouteId(routeId) : shipments.value;
    return source.filter((s: Shipment) => s.destinationAddressId === addressId);
  };

  const filterDeliveredShipments = (source: Shipment[]): Shipment[] => {
    const deliveredStatusCodeId = deliveredStatus.value?.id;
    if (!deliveredStatusCodeId || !Array.isArray(source) || source.length === 0) return [];

    return source.filter((shipment) => shipment.statusCodeId === deliveredStatusCodeId);
  };

  const filterLoadedAndDeliveredShipments = (source: Shipment[]): Shipment[] => {
    const deliveredStatusCodeId = deliveredStatus.value?.id;
    if (!Array.isArray(source) || source.length === 0) return [];
    if (pendingDeliveryStatusIds.value.size === 0 && !deliveredStatusCodeId) return [];

    return source.filter(
      (shipment) =>
        isPendingDeliveryStatus(shipment.statusCodeId) ||
        shipment.statusCodeId === deliveredStatusCodeId
    );
  };

  /** Top-level shipments only (not pieces inside a container). */
  const filterRootShipments = (source: Shipment[]): Shipment[] =>
    source.filter((s) => s.parentShipmentId == null);

  const getRootShipmentsByRouteAndDestinationId = (
    addressId: number,
    routeId?: number
  ): Shipment[] => {
    return filterRootShipments(getShipmentsByRouteAndDestinationId(addressId, routeId));
  };

  const getRootLoadedAndDeliveredShipmentsByRouteAndDestinationId = (
    addressId: number,
    routeId?: number
  ): Shipment[] => {
    const rows = getRootShipmentsByRouteAndDestinationId(addressId, routeId);
    return filterLoadedAndDeliveredShipments(rows);
  };

  const getRootDeliveredShipmentsCountByRouteAndDestinationId = (
    addressId: number,
    routeId?: number
  ): number => {
    const rows = getRootShipmentsByRouteAndDestinationId(addressId, routeId);
    return filterDeliveredShipments(rows).length;
  };

  const getLoadedAndDeliveredShipmentsByRouteAndDestinationId = (
    addressId: number,
    routeId?: number
  ): Shipment[] => {
    const shipmentsForDestination = getShipmentsByRouteAndDestinationId(addressId, routeId);
    return filterLoadedAndDeliveredShipments(shipmentsForDestination);
  };

  const getDeliveredShipmentsCountByRouteAndDestinationId = (
    addressId: number,
    routeId?: number
  ): number => {
    const shipmentsForDestination = getShipmentsByRouteAndDestinationId(addressId, routeId);
    return filterDeliveredShipments(shipmentsForDestination).length;
  };

  // Route-only derived
  const getDeliveredShipmentsForRoute = (routeId?: number): Shipment[] => {
    const source = routeId ? getShipmentsByRouteId(routeId) : shipments.value;
    return filterDeliveredShipments(source);
  };

  const getRootDeliveredShipmentsForRoute = (routeId?: number): Shipment[] => {
    return filterRootShipments(getDeliveredShipmentsForRoute(routeId));
  };

  const getRootDeliveredShipmentsCountForRoute = (routeId: number): number => {
    return getRootDeliveredShipmentsForRoute(routeId).length;
  };

  // === ACTIONS ===

  const loadShipments = async (): Promise<void> => {
    if (shipmentsQuery.isLoading.value) {
      return;
    }

    error.value = null;

    try {
      const shipmentsData = await fetchShipmentsFromApi();

      const updatedShipments = await upsertShipments(shipmentsData);

      shipments.value = updatedShipments;
    } catch (e) {
      // API failed - fallback to cache
      console.warn('[useShipments] API sync failed, falling back to cache:', e);

      const cachedShipments = await getAllShipmentsFromCache();
      if (cachedShipments && cachedShipments.length > 0) {
        // Cache has data - use it
        shipments.value = cachedShipments;
        error.value = null; // Don't set error if we have cache to fallback to
        return;
      }
      // No cache either - this is a real error
      error.value = e instanceof Error ? e : new Error('Failed to fetch shipments');
      throw e;
    }
  };

  const loadShipmentsByRouteId = async (routeId: number): Promise<Shipment[]> => {
    // Ensure all shipments are loaded first
    if (shipments.value.length === 0) {
      await loadShipments();
    }

    // Return shipments for the route using selector
    return getShipmentsByRouteId(routeId);
  };

  const isFirstDeliveryForRouteStop = (shipmentId: number, scannedAddressId: number): boolean => {
    const shipment = getShipmentById(shipmentId);
    if (!shipment) {
      throw new Error(`Shipment with id ${shipmentId} not found`);
    }

    // Check if this is the first delivery for this route stop (route + destination address)
    const deliveredCount = getRootDeliveredShipmentsCountByRouteAndDestinationId(
      scannedAddressId,
      shipment.routeId
    );
    return deliveredCount === 0;
  };

  type DeliveryScanContext = {
    scannedOnUtc: Date;
    scannedOnLocal: string;
    scannedTimeZoneId: number | undefined;
    deviceLocation: DeviceLocation | null;
  };

  const getTimeZoneConstantById = (id: number): { code?: string } | null => {
    try {
      return constantsActions.getById('TimeZoneConstants', id) as { code?: string } | null;
    } catch {
      return null;
    }
  };

  const loadDeliveryScanContext = async (
    scannedAddressId: number
  ): Promise<DeliveryScanContext> => {
    const scannedOnUtc = dateActions.utcNowDate();
    const scannedOnLocal = await dateActions.convertToAddressLocalTimeByAddressId(
      scannedOnUtc,
      scannedAddressId,
      getTimeZoneConstantById
    );
    const scannedAddress = await getAddressFromCacheById(scannedAddressId);
    // Get device location with maximum precision settings.
    // Will retry up to 2 times if accuracy is poor (>50m).
    const deviceLocation = await getDeviceLocation(
      {
        enableHighAccuracy: true,
        timeout: 20000, // 20 seconds for GPS satellite acquisition
        maximumAge: 0, // Always get fresh position - no cached values
      },
      2, // maxRetries
      50 // minAccuracy in meters (will retry if accuracy > 50m)
    );
    return {
      scannedOnUtc,
      scannedOnLocal,
      scannedTimeZoneId: scannedAddress?.timeZoneId,
      deviceLocation,
    };
  };

  const getChildShipmentIdsForParentDeliveryCascade = (
    parentShipmentId: number,
    deliveredStatusCodeId: number
  ): number[] => {
    const parent = getShipmentById(parentShipmentId);
    if (!parent?.routeId || parent.parentShipmentId != null) {
      return [];
    }
    const children = getShipmentsByRouteId(parent.routeId).filter(
      (child) =>
        child.parentShipmentId === parent.id && child.statusCodeId !== deliveredStatusCodeId
    );
    return children.map((child) => child.id);
  };

  const buildDeliveryShipmentUpdate = (
    deliveryScanContext: DeliveryScanContext,
    scannedAddressId: number,
    deliveredStatusCodeId: number,
    firstDelivery: boolean,
    scannedByUserAccountId: number | undefined
  ): ShipmentUpdate => ({
    statusCodeId: deliveredStatusCodeId,
    syncStatus: 'pending',
    scannedOn: deliveryScanContext.scannedOnUtc,
    scannedOnLocal: deliveryScanContext.scannedOnLocal,
    scannedAddressId,
    scannedByUserAccountId,
    trackingNumberInputTypeId: hplsInputType.value.id,
    firstDelivery,
    ...(deliveryScanContext.scannedTimeZoneId != null && {
      scannedTimeZoneId: deliveryScanContext.scannedTimeZoneId,
    }),
    ...(deliveryScanContext.deviceLocation && {
      scannedOnLatitude: deliveryScanContext.deviceLocation.latitude,
      scannedOnLongitude: deliveryScanContext.deviceLocation.longitude,
    }),
  });

  const deliverShipment = async (shipmentId: number, scannedAddressId: number): Promise<void> => {
    const deliveredStatusCodeId = deliveredStatus.value.id;
    const deliveryScanContext = await loadDeliveryScanContext(scannedAddressId);
    const firstDelivery = isFirstDeliveryForRouteStop(shipmentId, scannedAddressId);
    const scannedByUserAccountId = authStore.state.user?.userAccountId;
    const childShipmentIds = getChildShipmentIdsForParentDeliveryCascade(
      shipmentId,
      deliveredStatusCodeId
    );
    const changes = buildDeliveryShipmentUpdate(
      deliveryScanContext,
      scannedAddressId,
      deliveredStatusCodeId,
      firstDelivery,
      scannedByUserAccountId
    );
    await updateShipment([shipmentId, ...childShipmentIds], changes);
  };

  const setUndeliveredShipmentsAsNotOnTrailer = async (
    routeId: number,
    destinationAddressId: number
  ): Promise<void> => {
    try {
      const routeShipments = await getShipmentsFromCacheByRouteId(routeId);
      const deliveredStatusCodeId = deliveredStatus.value.id;

      const undeliveredShipments = routeShipments.filter(
        (shipment) =>
          shipment.destinationAddressId === destinationAddressId &&
          shipment.statusCodeId !== deliveredStatusCodeId
      );

      if (undeliveredShipments.length === 0) {
        return;
      }

      const now = dateActions.utcNowDate();

      for (const shipment of undeliveredShipments) {
        const changes: ShipmentUpdate = {
          statusCodeId: notOnTrailerStatus.value.id,
          updatedDate: now,
          syncStatus: 'pending',
        };

        await updateShipment(shipment.id, changes);
      }

      console.info(
        `[useShipments] Set ${undeliveredShipments.length} undelivered shipments as not on trailer for route ${routeId} and address ${destinationAddressId}`
      );
    } catch (error) {
      console.error('Failed to set undelivered shipments as not on trailer:', error);
      throw error;
    }
  };

  const completeRouteStop = async (
    routeId: number,
    destinationAddressId: number
  ): Promise<void> => {
    await setUndeliveredShipmentsAsNotOnTrailer(routeId, destinationAddressId);
  };

  // === LIFECYCLE ===

  onMounted(() => {
    trackSubscription(
      observeAllShipments().subscribe({
        next: (shipmentRows: Shipment[]) => {
          shipments.value = shipmentRows ?? [];
        },
        error: () => {
          // no-op: keep previous state
        },
      })
    );
  });

  // === COMPOSABLE OUTPUT ===

  const state = {
    shipments, // all shipments for the user
  };

  const selectors = {
    // Route-based
    getShipmentsByRouteId,
    getShipmentByRouteIdAndScanCode,
    getShipmentById,

    // Route + destination
    getShipmentsByRouteAndDestinationId,
    getLoadedAndDeliveredShipmentsByRouteAndDestinationId,
    getDeliveredShipmentsCountByRouteAndDestinationId,
    getRootShipmentsByRouteAndDestinationId,
    getRootLoadedAndDeliveredShipmentsByRouteAndDestinationId,
    getRootDeliveredShipmentsCountByRouteAndDestinationId,

    // Route-only derived
    getDeliveredShipmentsForRoute,
    getRootDeliveredShipmentsForRoute,
    getRootDeliveredShipmentsCountForRoute,

    // Status helpers
    isPendingDeliveryStatus,
  };

  const actions = {
    loadShipments,
    deliverShipment,
    loadShipmentsByRouteId,
    completeRouteStop,
  };

  const status = {
    isLoading: computed(() => shipmentsQuery.isLoading.value),
    error: computed(() => error.value || shipmentsQuery.error.value),
  };

  return {
    state,
    selectors,
    actions,
    status,
  };
};
